"use client"

import type { MouseEvent } from "react"

import { flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table"

import type { ColumnDef } from "@tanstack/react-table"

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { cn } from "@/lib/utils"

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  className?: string
  tableClassName?: string
  enableColumnResizing?: boolean
  onRowClick?: (rowIndex: number, event: MouseEvent) => void
  isRowSelected?: (rowIndex: number) => boolean
  onHeaderClick?: (columnId: string) => void
  isHeaderSelected?: (columnId: string) => boolean
  selectMode?: boolean
}

export function DataTable<TData, TValue>({
  columns,
  data,
  className,
  tableClassName,
  enableColumnResizing = false,
  onRowClick,
  isRowSelected,
  onHeaderClick,
  isHeaderSelected,
  selectMode = false,
}: DataTableProps<TData, TValue>) {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableColumnResizing,
    columnResizeMode: "onChange",
  })

  return (
    <div className={cn("overflow-hidden rounded-md border", className)}>
      <Table className="table-fixed" parentClassName={cn("flex flex-col", tableClassName)}>
        <TableHeader className="bg-background sticky top-0 z-10 [&_tr]:border-b">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header, headerIndex) => {
                const isLastColumn = headerIndex === headerGroup.headers.length - 1
                const columnSize = header.column.getSize()
                const canResize = enableColumnResizing && header.column.getCanResize()
                const columnId = header.column.id
                const isSelected = isHeaderSelected?.(columnId)
                const isClickable = selectMode && onHeaderClick && columnId !== "documentName"

                return (
                  <TableHead
                    key={header.id}
                    style={{ width: `${columnSize}px` }}
                    className={cn(
                      !isLastColumn && "border-r",
                      "relative p-0",
                      isSelected && "bg-primary/10",
                      isClickable && "hover:bg-muted/50 cursor-pointer"
                    )}
                    onClick={isClickable ? () => onHeaderClick(columnId) : undefined}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                    {canResize && (
                      <div
                        role="presentation"
                        className={cn(
                          "absolute top-0 right-0 h-full w-1 cursor-col-resize touch-none select-none",
                          "hover:bg-primary/50 bg-transparent",
                          header.column.getIsResizing() && "bg-primary"
                        )}
                        onDoubleClick={() => header.column.resetSize()}
                        onMouseDown={header.getResizeHandler()}
                        onTouchStart={header.getResizeHandler()}
                      />
                    )}
                  </TableHead>
                )
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length ? (
            table.getRowModel().rows.map((row, rowIndex) => {
              const isSelected = isRowSelected?.(rowIndex)
              const isClickable = selectMode && onRowClick

              return (
                <TableRow
                  key={row.id}
                  className={cn(
                    isSelected && "bg-primary/10",
                    isClickable && "hover:bg-muted/50 cursor-pointer"
                  )}
                  onClick={isClickable ? (event) => onRowClick(rowIndex, event) : undefined}
                >
                  {row.getVisibleCells().map((cell, cellIndex) => {
                    const isLastColumn = cellIndex === row.getVisibleCells().length - 1
                    const columnSize = cell.column.getSize()
                    return (
                      <TableCell
                        key={cell.id}
                        className={cn(!isLastColumn && "border-r", "p-2")}
                        style={{ width: `${columnSize}px` }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    )
                  })}
                </TableRow>
              )
            })
          ) : (
            <TableRow>
              <TableCell className="h-24 text-center" colSpan={columns.length}>
                No results.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  )
}
